import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { Subscription } from "rxjs";
import { NgTemplateOutlet } from "@angular/common";
import { CdkScrollable } from "@angular/cdk/overlay";

/**
 * Tab strip + active panel.
 *
 *   <bv-tabs [(selected)]="active">
 *     <bv-tab label="Overview">…content…</bv-tab>
 *     <bv-tab label="Players">…content…</bv-tab>
 *     <bv-tab label="Settings" [disabled]="!isOwner">…content…</bv-tab>
 *   </bv-tabs>
 */
@Component({
    selector: "bv-tabs",
    templateUrl: "./tabs.component.html",
    styleUrls: ["./tabs.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "[class.bv-tabs--sticky]": "sticky",
    },
    imports: [NgTemplateOutlet, CdkScrollable]
})
export class BvTabsComponent implements AfterContentInit, OnDestroy {
  @ContentChildren(forwardRefTab(), { descendants: false })
  tabs!: QueryList<BvTabComponent>;

  @Input() selected = 0;
  @Output() selectedChange = new EventEmitter<number>();

  /**
   * When true, the tab strip stays pinned to the top while the panel
   * scrolls beneath. Useful on long-form routes (e.g. tournament views)
   * where the tabs should remain accessible while the user scrolls.
   * Accepts the standalone-attribute form (`<bv-tabs sticky>`) via the
   * boolean coercion below.
   */
  @Input()
  get sticky(): boolean { return this._sticky; }
  set sticky(v: any) { this._sticky = coerceBooleanProperty(v); }
  private _sticky = false;

  private tabsSub?: Subscription;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.tabsSub = this.tabs.changes.subscribe(() => this.cdr.markForCheck());
  }

  ngOnDestroy(): void {
    this.tabsSub?.unsubscribe();
  }

  get activeTab(): BvTabComponent | undefined {
    return this.tabs?.toArray()[this.selected];
  }

  selectIndex(i: number): void {
    if (i === this.selected) return;
    const target = this.tabs?.toArray()[i];
    if (!target || target.disabled) return;
    this.selected = i;
    this.selectedChange.emit(i);
  }
}

/**
 * Custom tab-strip label. Apply to an `<ng-template>` inside a `<bv-tab>`
 * to render icons or other markup in the tab button instead of the
 * plain `label` text.
 *
 *   <bv-tab>
 *     <ng-template bvTabLabel>
 *       <bv-icon name="people" size="sm"></bv-icon>
 *       <span>Players</span>
 *     </ng-template>
 *     ...content...
 *   </bv-tab>
 */
@Directive({ selector: "[bvTabLabel]" })
export class BvTabLabelDirective {
  constructor(public template: TemplateRef<unknown>) {}
}

@Component({
    selector: "bv-tab",
    // Two named templates:
    //   #content  → the tab panel body (projected via <ng-content>)
    //   #defaultLabel → fallback label rendering when no [bvTabLabel] is
    //                   projected. Living here (not in bv-tabs) means
    //                   {{ label }} re-evaluates in bv-tab's CD context
    //                   when the input changes asynchronously (e.g. the
    //                   translate pipe resolving on page refresh).
    template: `
    <ng-template #content><ng-content></ng-content></ng-template>
    <ng-template #defaultLabel>{{ label }}</ng-template>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvTabComponent {
  @Input() label = "";
  @Input() disabled = false;

  @ViewChild("content", { static: true })
  contentTpl!: TemplateRef<unknown>;

  @ViewChild("defaultLabel", { static: true })
  defaultLabelTpl!: TemplateRef<unknown>;

  @ContentChild(BvTabLabelDirective)
  labelDir?: BvTabLabelDirective;
}

// forwardRef helper avoids the circular reference between
// ContentChildren(BvTabComponent) and the BvTabComponent class
// being declared later in the same file.
function forwardRefTab(): typeof BvTabComponent {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return BvTabComponent;
}
